import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { generateFormField, validateRequired, getFormFields } from '../../utils/schemaToForm.jsx';
import { getEnrichedSnapshot } from '../../utils/mstSnapshotEnricher';
import './SmartModal.css';

const SmartModal = observer(({ 
    isOpen,
    onClose,
    onSave,
    item,                   // Объект для редактирования (null для создания)
    schema,                 // JSON Schema модели
    modelName,              // Название модели для заголовка
    isCreating,            // Флаг создания/редактирования
    modalTitle,            // Кастомный заголовок (опционально)
    models,                 // Для получения enum значений
    mode = 'form',          // Режим работы: 'form' | 'viewer' | 'custom'
    children,               // Контент для режимов viewer и custom
    hideFooter,             // Скрыть footer
    size,                   // Размер модалки
    parentContext           // Контекст родителя для наследования полей
}) => {
    
    // Универсальный резолвер источников данных для наследования
    const resolveInheritanceSource = (sourceName) => {
        if (!sourceName) return null;

        // Специальный случай: если sourceName === "project" и есть parentContext - используем его
        // (при создании расчета из проекта)
        if (sourceName === 'project' && parentContext) {
            console.log('[SmartModal] Using parentContext as project source:', parentContext);
            return parentContext;
        }

        // Если есть parentContext и источник совпадает с именем родителя
        if (parentContext && sourceName === parentContext._modelName) {
            return parentContext;
        }

        // Пытаемся найти в models (для моделей-синглтонов типа system)
        // Исключаем известные не-коллекции
        const nonCollections = ['session', 'auth', 'user', 'current', 'project'];
        if (!nonCollections.includes(sourceName)) {
            const collection = models?.getCollectionManager?.(sourceName);
            if (collection?.items?.length > 0) {
                return collection.items[0];
            }
        }

        // Пытаемся найти в корневом сторе (для auth, session и т.д.)
        // models - это MSTModelStore, нужно подняться к rootStore
        const rootStore = models?.rootStore || (models?.$treenode?.root?.storedValue);

        if (rootStore && sourceName in rootStore) {
            return rootStore[sourceName];
        }

        // Если не нашли точное совпадение, пробуем алиасы
        const aliases = {
            'session': 'auth',
            'user': 'auth',
            'current': 'auth'
        };

        if (aliases[sourceName] && rootStore?.[aliases[sourceName]]) {
            return rootStore[aliases[sourceName]];
        }

        return null;
    };
    
    // Функция для получения наследуемых значений на основе схемы
    const getInheritedValues = (schema, parentContext) => {
        const inherited = {};

        if (!isCreating) return inherited;

        console.log('[getInheritedValues] Starting inheritance resolution for:', schema.$id);
        console.log('[getInheritedValues] parentContext:', parentContext);

        // Проходим по всем свойствам схемы
        Object.entries(schema.properties || {}).forEach(([field, fieldSchema]) => {
            // Обработка полей с x-inherited
            if (fieldSchema['x-inherited'] && fieldSchema['x-inheritsFrom']) {
                const source = fieldSchema['x-inheritsFrom'];
                const inheritance = fieldSchema['x-inheritance'] || {};

                console.log(`[getInheritedValues] Field "${field}" inherits from "${source}":`, inheritance);

                // Универсальное получение источника данных
                const sourceData = resolveInheritanceSource(source);

                if (sourceData) {
                    // Если есть маппинг полей
                    if (Object.keys(inheritance).length > 0) {
                        // Проверяем, это простой маппинг или объект
                        const firstValue = Object.values(inheritance)[0];
                        if (typeof firstValue === 'string' && Object.keys(inheritance).length === 1 && Object.keys(inheritance)[0] === field) {
                            // Простой маппинг типа { "hourRate": "settings.defaultRate" }
                            const sourceField = firstValue;

                            // Если sourceField содержит точку, это путь к вложенному полю
                            let value;
                            if (sourceField.includes('.')) {
                                value = getValueByPath(sourceData, sourceField);
                            } else {
                                value = sourceData[sourceField];
                            }

                            if (value !== undefined) {
                                inherited[field] = value;
                                console.log(`[getInheritedValues] Inherited ${field} = ${value}`);
                            } else {
                                console.log(`[getInheritedValues] Could not get value for ${field} from path ${sourceField}`);
                            }
                        } else {
                            // Маппинг для объекта типа { "defaultRate": "defaultRate", "vatIncluded": "vatIncluded" }
                            inherited[field] = {};
                            Object.entries(inheritance).forEach(([targetField, sourceField]) => {
                                let value;
                                if (sourceField.includes('.')) {
                                    value = getValueByPath(sourceData, sourceField);
                                } else {
                                    value = sourceData[sourceField];
                                }

                                if (value !== undefined) {
                                    inherited[field][targetField] = value;
                                }
                            });
                        }
                    } else {
                        // Если маппинга нет, копируем поле целиком
                        if (sourceData[field] !== undefined) {
                            inherited[field] = sourceData[field];
                        }
                    }
                }
            }
        });
        
        // Обработка x-business-logic (для calculations)
        const businessLogic = schema['x-business-logic']?.['settings-inheritance']?.onCreate;
        if (businessLogic && parentContext) {
            console.log('[getInheritedValues] Processing business logic inheritance:', businessLogic);
            Object.entries(businessLogic).forEach(([targetField, sourcePath]) => {
                // Парсим путь и получаем значение
                const value = getValueByPath(parentContext, sourcePath);
                console.log(`[getInheritedValues] Business logic: ${targetField} from path "${sourcePath}" = ${value}`);
                if (value !== undefined) {
                    inherited[targetField] = value;
                }
            });
        }

        console.log('[getInheritedValues] Final inherited values:', inherited);
        return inherited;
    };
    
    // Вспомогательная функция для получения значения по пути
    const getValueByPath = (obj, path) => {
        // Убираем префикс "project." если есть
        const cleanPath = path.startsWith('project.') ? path.substring(8) : path;
        const parts = cleanPath.split('.');
        
        let current = obj;
        for (const part of parts) {
            if (current && typeof current === 'object' && part in current) {
                current = current[part];
            } else {
                return undefined;
            }
        }
        return current;
    };
    
    // Инициализация formData из схемы - только для режима form
    const [formData, setFormData] = useState(() => {
        if (mode !== 'form' || !schema) return {};
        
        const formFields = getFormFields(schema, { isCreating });
        const initialData = {};
        const inheritedValues = getInheritedValues(schema, parentContext);
        
        formFields.forEach(field => {
            const fieldSchema = schema.properties[field];

            // Приоритет: 1) item (редактирование), 2) наследование, 3) default, 4) первое значение enum
            if (item && item[field] !== undefined) {
                initialData[field] = item[field];
            } else if (isCreating && inheritedValues[field] !== undefined) {
                initialData[field] = inheritedValues[field];
            } else if (fieldSchema.default !== undefined) {
                initialData[field] = fieldSchema.default;
            } else if (fieldSchema.enum && fieldSchema.enum.length > 0) {
                // Для enum полей без default берем первое значение
                initialData[field] = fieldSchema.enum[0];
            }
        });
        
        return initialData;
    });
    
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    
    // Сброс формы при изменении item, parentContext или создании нового
    useEffect(() => {
        if (!schema) return;
        
        const formFields = getFormFields(schema, { isCreating });
        const newData = {};
        const inheritedValues = getInheritedValues(schema, parentContext);
        
        formFields.forEach(field => {
            const fieldSchema = schema.properties[field];
            // Приоритет: 1) item (редактирование), 2) наследование, 3) default, 4) первое значение enum
            if (item && item[field] !== undefined) {
                newData[field] = item[field];
            } else if (isCreating && inheritedValues[field] !== undefined) {
                newData[field] = inheritedValues[field];
            } else if (fieldSchema.default !== undefined) {
                newData[field] = fieldSchema.default;
            } else if (fieldSchema.enum && fieldSchema.enum.length > 0) {
                // Для enum полей без default берем первое значение
                newData[field] = fieldSchema.enum[0];
            }
        });
        
        setFormData(newData);
        setErrors({});
    }, [item, isCreating, schema, parentContext]);
    
    const validate = () => {
        if (!schema) {
            console.error(`${modelName} schema not loaded`);
            return false;
        }

        // Формируем список required полей с учетом x-ui-requiredWhen
        const requiredFields = [...(schema.required || [])];

        Object.keys(formData).forEach(field => {
            const prop = schema.properties[field];
            if (prop['x-ui-requiredWhen'] === 'creating' && isCreating) {
                requiredFields.push(field);
            } else if (prop['x-ui-requiredWhen'] === 'editing' && !isCreating) {
                requiredFields.push(field);
            }
        });

        // Для создания: добавляем наследуемые значения для полей с x-ui-form: false
        let dataForValidation = { ...formData };

        if (isCreating) {
            const allInheritedValues = getInheritedValues(schema, parentContext);

            // Добавляем наследуемые значения для обязательных полей, которые не в форме
            requiredFields.forEach(field => {
                const fieldSchema = schema.properties[field];
                if (fieldSchema && fieldSchema['x-ui-form'] === false && allInheritedValues[field] !== undefined) {
                    dataForValidation[field] = allInheritedValues[field];
                }
            });

            // Также добавляем projectId из item, если он там есть (передается через modal.data)
            if (item?.projectId && !dataForValidation.projectId) {
                dataForValidation.projectId = item.projectId;
            }
        }

        console.log('[SmartModal] Validation check:');
        console.log('  Required fields:', requiredFields);
        console.log('  Data for validation:', dataForValidation);

        // Валидация обязательных полей
        const schemaForValidation = { ...schema, required: requiredFields };
        const newErrors = validateRequired(schemaForValidation, dataForValidation);

        console.log('[SmartModal] Validation errors:', newErrors);
        
        // Проверка x-ui-confirmFor (например, подтверждение пароля)
        Object.keys(formData).forEach(field => {
            const fieldSchema = schema.properties[field];
            if (fieldSchema['x-ui-confirmFor']) {
                const targetField = fieldSchema['x-ui-confirmFor'];
                if (formData[field] && formData[targetField] && formData[field] !== formData[targetField]) {
                    const label = fieldSchema['x-ui-formLabel'] || field;
                    const targetLabel = schema.properties[targetField]['x-ui-formLabel'] || targetField;
                    newErrors[field] = `${label} не совпадает с ${targetLabel}`;
                }
            }
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setIsLoading(true);

        try {
            // При создании добавляем наследуемые поля, которые могут быть скрыты в форме
            let dataToSave = { ...formData };

            console.log('[SmartModal handleSubmit] Initial item:', item);
            console.log('[SmartModal handleSubmit] formData:', formData);
            console.log('[SmartModal handleSubmit] isCreating:', isCreating);

            // При редактировании добавляем ID из исходного объекта
            if (!isCreating && item?.id) {
                dataToSave.id = item.id;
            }

            if (isCreating) {
                // Получаем ВСЕ наследуемые значения, включая скрытые поля
                const allInheritedValues = getInheritedValues(schema, parentContext);

                // Добавляем наследуемые значения, которых нет в formData
                Object.entries(allInheritedValues).forEach(([field, value]) => {
                    // Если поле не в форме (x-ui-form: false) или readonly, добавляем его
                    const fieldSchema = schema.properties[field];
                    if (fieldSchema && (fieldSchema['x-ui-form'] === false || fieldSchema['x-ui-formReadonly'])) {
                        dataToSave[field] = value;
                    }
                    // Также добавляем, если поле пустое
                    else if (dataToSave[field] === undefined || dataToSave[field] === null || dataToSave[field] === '') {
                        dataToSave[field] = value;
                    }
                });

                // Добавляем поля из item которые не в форме (для копирования данных при создании)
                // Это важно для создания шаблонов из расчетов - нужно передать content
                if (item) {
                    Object.entries(item).forEach(([field, value]) => {
                        // Пропускаем служебные поля и уже добавленные
                        if (dataToSave[field] === undefined && value !== undefined && value !== null) {
                            // Для templates нужно передать content и другие данные из расчета
                            dataToSave[field] = value;
                        }
                    });
                }
            }

            console.log('[SmartModal handleSubmit] Final dataToSave:', dataToSave);
            console.log('[SmartModal handleSubmit] dataToSave.content:', dataToSave.content);

            await onSave(dataToSave);
            // НЕ вызываем onClose - SmartModalContainer сам закроет при успехе
        } catch (error) {
            console.error(`Error saving ${modelName}:`, error);
            setErrors({ 
                _form: error.response?.data?.message || error.message || 'Произошла ошибка при сохранении'
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleChange = (e) => {
        const { name, value, type, checked, templateData, templateConfig } = e.target;

        // Специальная обработка для template-selector
        if (type === 'template-selector' && templateData) {
            const fieldsToLoad = templateConfig?.loadFields || [];
            const copyNamePattern = templateConfig?.copyNameAs;

            // Получаем snapshot с вычисляемыми полями
            const enrichedTemplate = getEnrichedSnapshot(templateData, schema.properties);

            // Обновляем formData с полями из template
            setFormData(prev => {
                const updated = { ...prev, [name]: value };

                // Копируем указанные поля
                fieldsToLoad.forEach(field => {
                    if (enrichedTemplate[field] !== undefined) {
                        updated[field] = enrichedTemplate[field];
                    }
                });

                // Обрабатываем название
                if (copyNamePattern && templateData.templateName) {
                    updated.name = copyNamePattern.replace('${templateName}', templateData.templateName);
                }

                return updated;
            });

            // Очищаем ошибку
            if (errors[name]) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
            return;
        }

        // Обычная обработка
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Очищаем ошибку при изменении поля
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    
    // Функция для рендеринга группы полей
    const renderFieldsGroup = (fields) => {
        const rows = [];
        let currentRow = [];
        
        fields.forEach((field, index) => {
            const fieldSchema = { ...schema.properties[field] };
            
            // Определяем обязательность поля
            let isRequired = schema.required?.includes(field);
            if (!isRequired && fieldSchema['x-ui-requiredWhen']) {
                isRequired = (fieldSchema['x-ui-requiredWhen'] === 'creating' && isCreating) ||
                           (fieldSchema['x-ui-requiredWhen'] === 'editing' && !isCreating);
            }
            fieldSchema.required = isRequired;
            
            // Определяем disabled состояние из схемы
            let isDisabled = isLoading || fieldSchema['x-ui-formReadonly'] === true;
            
            // Проверяем, не редактирует ли пользователь сам себя
            if (fieldSchema['x-ui-selfReadOnly'] && modelName === 'users') {
                const currentUserId = models?.auth?.user?.id;
                if (currentUserId && initialData?.id === currentUserId) {
                    isDisabled = true;
                }
            }
            
            const fieldElement = generateFormField(
                field,
                fieldSchema,
                formData[field],
                handleChange,
                errors,
                isDisabled,
                isCreating,
                models,
                formData
            );
            
            const column = fieldSchema['x-ui-formColumn'];
            
            if (!column || column === 'full') {
                // Полноширинное поле
                if (currentRow.length > 0) {
                    rows.push(
                        <div key={`row-${rows.length}`} className="form-row">
                            {currentRow}
                        </div>
                    );
                    currentRow = [];
                }
                rows.push(fieldElement);
            } else {
                // Поле с колонкой
                currentRow.push(
                    <div key={field} className={`form-col form-col-${column}`}>
                        {fieldElement}
                    </div>
                );
                
                // Если 2 поля в строке или последнее поле
                if (currentRow.length === 2 || index === fields.length - 1) {
                    rows.push(
                        <div key={`row-${rows.length}`} className="form-row">
                            {currentRow}
                        </div>
                    );
                    currentRow = [];
                }
            }
        });
        
        return rows;
    };
    
    if (!isOpen) return null;
    
    // Проверка schema только для режима form
    if (mode === 'form' && !schema) {
        console.error(`Schema for ${modelName} not loaded`);
        return (
            <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
                <div className="modal modal-large">
                    <div className="modal-header">
                        <h3>Ошибка</h3>
                        <button className="btn-close" onClick={onClose}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <p>Не удалось загрузить схему модели {modelName}</p>
                    </div>
                </div>
            </div>
        );
    }
    
    // Получаем поля для отображения в форме (только для mode='form')
    const formFields = mode === 'form' && schema ? getFormFields(schema, { isCreating }) : [];
    
    // Группируем поля по секциям
    const sections = {};
    const unsectionedFields = [];
    
    formFields.forEach(field => {
        const fieldSchema = schema.properties[field];
        const section = fieldSchema['x-ui-formSection'];
        
        if (section) {
            if (!sections[section]) {
                sections[section] = {
                    fields: [],
                    icon: fieldSchema['x-ui-formSectionIcon'] || '📋',
                    order: fieldSchema['x-ui-formSectionOrder'] || 999
                };
            }
            sections[section].fields.push(field);
        } else {
            unsectionedFields.push(field);
        }
    });
    
    // Сортируем секции по порядку
    const sortedSections = Object.entries(sections)
        .sort(([, a], [, b]) => a.order - b.order);
    
    // Определяем заголовок модалки из схемы или используем дефолтный
    const title = modalTitle || 
                  (mode === 'form' && isCreating ? `Создание ${schema?.title || modelName}` : 
                   mode === 'form' && !isCreating ? `Редактирование ${schema?.title || modelName}` :
                   modelName || 'Просмотр');
    
    // Функция для рендера body в зависимости от mode
    const renderModalBody = () => {
        switch (mode) {
            case 'form':
                return (
                    <>
                        {errors._form && (
                            <div className="form-error-message">
                                {errors._form}
                            </div>
                        )}
                        
                        <div className="form-grid">
                            {/* Поля без секций */}
                            {unsectionedFields.length > 0 && (
                                <div className="form-section-content">
                                    {renderFieldsGroup(unsectionedFields)}
                                </div>
                            )}
                        
                            {/* Секции с полями */}
                            {sortedSections.map(([sectionName, section]) => (
                                <div key={sectionName} className="form-section">
                                    <div className="form-section-header">
                                        <span className="form-section-icon">{section.icon}</span>
                                        <h3>{sectionName}</h3>
                                    </div>
                                    <div className="form-section-content">
                                        {renderFieldsGroup(section.fields)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                );
            
            case 'viewer':
            case 'custom':
                return children;
            
            default:
                return null;
        }
    };
    
    // Функция для рендера footer в зависимости от mode и hideFooter
    const renderModalFooter = () => {
        if (hideFooter) return null;
        
        if (mode === 'form') {
            return (
                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Отмена
                    </button>
                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isLoading}
                    >
                        {isLoading ? 'Сохранение...' : (isCreating ? 'Создать' : 'Сохранить')}
                    </button>
                </div>
            );
        }
        
        // Для viewer и custom режимов - только кнопка закрытия
        return (
            <div className="modal-footer">
                <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={onClose}
                >
                    Закрыть
                </button>
            </div>
        );
    };
    
    // Определяем нужна ли форма
    const needsForm = mode === 'form';
    
    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`modal ${size ? `modal-${size}` : 'modal-large'}`}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button
                        className="btn-close"
                        onClick={onClose}
                        type="button"
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            width: '28px',
                            height: '28px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            background: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            lineHeight: '1',
                            color: '#6b7280',
                            padding: 0,
                            margin: 0,
                            zIndex: 10,
                            transition: 'all 0.2s'
                        }}
                        aria-label="Закрыть"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#ef4444';
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.color = '#6b7280';
                        }}
                    >
                        ×
                    </button>
                </div>
                
                {needsForm ? (
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {renderModalBody()}
                        </div>
                        {renderModalFooter()}
                    </form>
                ) : (
                    <>
                        <div className="modal-body">
                            {renderModalBody()}
                        </div>
                        {renderModalFooter()}
                    </>
                )}
                
                {isLoading && (
                    <div className="modal-loading">
                        <div className="spinner"></div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default SmartModal;